import React, { useEffect, useRef, useState } from "react";
import { IconEdit, IconClose, IconPeople } from "./Icons";
import { describeError } from "./errors";
import "./SkillsPanel.css";

const TIER_LABEL = { personal: "Personal", role: "Role", global: "Global" };

/**
 * The caller's skill library: their personal skills plus role/global skills
 * an admin published that apply to them. Active skills (per-user toggle) load
 * into every new chat the caller starts.
 */
export default function SkillsPanel({
  skills, loading, activeCount, isAdmin = false,
  onToggle, onEdit, onDelete, onNew, onUploadZip,
  lockedSkillIds = null,
}) {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  // Upload errors surface here (no panel-level error UI existed before). A 400
  // like "a skill must contain exactly one root SKILL.md file" lands here.
  const [uploadError, setUploadError] = useState(null);
  // Toggling a skill mid-chat takes effect only on the NEXT message (server-side
  // rebind, Task 10). Surface a transient hint so users aren't surprised the
  // current turn is unaffected.
  const [toggleHint, setToggleHint] = useState(false);
  const hintTimerRef = useRef(null);

  // Clear any pending hint timer on unmount to avoid setState-after-unmount.
  useEffect(() => () => {
    if (hintTimerRef.current) clearTimeout(hintTimerRef.current);
  }, []);

  const handleToggle = (id, checked) => {
    // Don't block/delay the optimistic toggle in the parent hook — fire it first.
    onToggle(id, checked);
    setToggleHint(true);
    // Re-arm the auto-hide timer; clear any prior one so rapid toggles don't stack.
    if (hintTimerRef.current) clearTimeout(hintTimerRef.current);
    hintTimerRef.current = setTimeout(() => {
      setToggleHint(false);
      hintTimerRef.current = null;
    }, 4000);
  };

  const handleUploadClick = () => {
    if (uploading) return;
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setUploading(true);
    setUploadError(null);
    try {
      // onUploadZip (useSkills.uploadSkillZip) refreshes the list on success.
      await onUploadZip(file);
    } catch (err) {
      setUploadError(describeError(err) || "Upload failed.");
    } finally {
      setUploading(false);
      // Reset so picking the same file again still fires onChange.
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const locked = lockedSkillIds !== null;
  if (locked) {
    const lockedSet = new Set(lockedSkillIds);
    const lockedSkills = skills.filter((s) => lockedSet.has(s._id));
    return (
      <div className="skills-panel">
        <div className="sp-locked-note">
          Skills are locked for this conversation. Start a new chat to change them.
        </div>
        <div className="sp-body">
          {lockedSkills.length === 0 && (
            <div className="sp-empty">No skills active in this conversation.</div>
          )}
          {lockedSkills.map((s) => (
            <div key={s._id} className="sp-row active shared">
              <div className="sp-row-edit" style={{ cursor: "default" }}>
                <div className="sp-name-line">
                  <span className="sp-name">{s.name}</span>
                  <span className={`sp-tier-badge sp-tier-badge--${s.scope || "personal"}`}>
                    {(s.scope && s.scope !== "personal") ? <IconPeople size={11} /> : null}
                    {TIER_LABEL[s.scope] || s.scope || "Personal"}
                  </span>
                </div>
                {s.description && <div className="sp-desc">{s.description}</div>}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="skills-panel">
      <div className="sp-summary">
        <span>
          <strong>{activeCount}</strong> active of <strong>{skills.length}</strong>
        </span>
        <div className="sp-summary-actions">
          <input
            ref={fileInputRef}
            type="file"
            accept=".zip,application/zip"
            className="sp-file-input"
            onChange={handleFileChange}
            aria-hidden="true"
            tabIndex={-1}
          />
          <button
            className="ca-btn ca-btn--sm"
            onClick={handleUploadClick}
            disabled={uploading}
            title="Upload a .zip skill bundle (must contain one root SKILL.md)"
            aria-label="Upload a .zip skill bundle"
          >
            {uploading ? "Uploading…" : "Upload .zip"}
          </button>
          <button className="ca-btn ca-btn--sm ca-btn--primary" onClick={onNew}>+ New Skill</button>
        </div>
      </div>
      {uploadError && (
        <div className="sp-error" role="alert">{uploadError}</div>
      )}
      {toggleHint && (
        <div className="sp-hint" role="status">
          Skill changes apply starting with your next message.
        </div>
      )}
      <div className="sp-body">
        {loading && <div className="sp-empty">Loading…</div>}
        {!loading && skills.length === 0 && (
          <div className="sp-empty">
            <strong>No skills yet.</strong>
            <div className="sp-empty-hint">
              Click <strong>+ New Skill</strong> to start from a template or write your own,
              or <strong>Upload .zip</strong> to import a multi-file skill bundle.
              {isAdmin && " As an admin you can also publish role-type and global skills."}
            </div>
          </div>
        )}
        {skills.map((s) => {
          const canEdit = s.can_edit !== false;
          const scope = s.scope || "personal";
          const syncFailed = s.sync_state === "error";
          const fileCount = Number(s.file_count) || 0;
          return (
            <div key={s._id} className={`sp-row ${s.is_active ? "active" : "inactive"} ${canEdit ? "owned" : "shared"}`}>
              <label
                className="sp-toggle-row"
                title={s.is_active ? "On — loads into every new chat" : "Off — won't load until you switch it on"}
              >
                <input type="checkbox" checked={!!s.is_active}
                       onChange={(e) => handleToggle(s._id, e.target.checked)} />
              </label>
              <button className="sp-row-edit" onClick={() => onEdit(s._id)} title="Open">
                <div className="sp-name-line">
                  <span className="sp-name">{s.name}</span>
                  <span className={`sp-tier-badge sp-tier-badge--${scope}`}>
                    {scope === "personal" ? null : <IconPeople size={11} />}
                    {TIER_LABEL[scope] || scope}
                  </span>
                  {syncFailed && (
                    <span
                      className="sp-sync-badge sp-sync-badge--error"
                      title="This skill failed to sync to the Skills API — it isn't usable yet. Re-save or re-upload to retry."
                    >
                      ⚠ sync failed
                    </span>
                  )}
                  {fileCount > 1 && (
                    <span className="sp-file-count" title={`${fileCount} files in this skill`}>
                      {fileCount} files
                    </span>
                  )}
                </div>
                {s.description && <div className="sp-desc">{s.description}</div>}
                {scope === "role" && s.roles?.length > 0 && (
                  <div className="sp-share-hint">Roles: {s.roles.join(", ")}</div>
                )}
              </button>
              {canEdit && (
                <div className="sp-row-actions">
                  <button className="sp-row-action" onClick={() => onEdit(s._id)}
                          title="Edit skill" aria-label="Edit skill"><IconEdit size={14} /></button>
                  <button className="sp-row-action sp-row-action--danger"
                          onClick={() => {
                            if (window.confirm(`Delete skill "${s.name}"?`)) {
                              Promise.resolve(onDelete(s._id)).catch((e) =>
                                window.alert(describeError(e) || "Delete failed."));
                            }
                          }}
                          title="Delete skill" aria-label="Delete skill"><IconClose size={14} /></button>
                </div>
              )}
            </div>
          );
        })}
        {!loading && skills.length > 0 && (
          <div className="sp-footer">Active skills load into every new chat you start.</div>
        )}
      </div>
    </div>
  );
}
